import { notification } from "antd";
import { defineMessages } from "react-intl";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import Organization from "@mapmycustomers/shared/types/Organization";
import UserLogin from "@mapmycustomers/shared/types/userLogin/UserLogin";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";

import { fetchSessions, logoutOtherSessions, logoutSession } from "./actions";

const messages = defineMessages({
  sessionLogoutError: {
    id: "settings.sessions.sessionLogout.error",
    defaultMessage: "Error logging out of device",
    description: "Settings sessions page - text of failure notification when session logout error",
  },
  sessionLogoutErrorDescription: {
    id: "settings.sessions.sessionLogout.errorDescription",
    defaultMessage: "Please try again, or log out from your physical device.",
    description:
      "Settings sessions page - description of failure notification when session logout error",
  },
  sessionLogoutSuccess: {
    id: "settings.sessions.sessionLogout.success",
    defaultMessage: "Logged out of device",
    description:
      "Settings sessions page - text of success notification when session logout successful",
  },
});

function* onSessionsFetch({
  payload: { callback, failureCallback },
}: ReturnType<typeof fetchSessions>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const userLoginsResponse: ListResponse<UserLogin> = yield callApi("getUserLogins", orgId);

    yield call(callback, userLoginsResponse.data);
  } catch (error) {
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(handleError({ error }));
  }
}

function* onSessionLogout({
  payload: { id, callback, failureCallback },
}: ReturnType<typeof logoutSession>) {
  const intl = i18nService.getIntl();
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    yield callApi("deleteToken", orgId, id);

    yield call(callback);

    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.sessionLogoutSuccess),
      });
    }
  } catch (error) {
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.sessionLogoutError),
        message: intl.formatMessage(messages.sessionLogoutErrorDescription),
      });
    }

    yield call(failureCallback);
    yield put(handleError({ displayNotification: false, error }));
  }
}

function* onOtherSessionsLogout({ payload }: ReturnType<typeof logoutOtherSessions>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    yield callApi("deleteOtherTokens", orgId);

    yield call(payload.callback);
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* sessionsSaga() {
  yield takeLatest(fetchSessions, onSessionsFetch);
  yield takeEvery(logoutSession, onSessionLogout);
  yield takeEvery(logoutOtherSessions, onOtherSessionsLogout);
}
